import {
    ClipboardCheck,
    Factory,
    Globe,
    PackageSearch,
    Ship,
    ShoppingCart,
    Target,
    Truck,
    ArrowUpRight,
} from "lucide-react";
import { useLanguage } from "@/context/LanguageContext";
import { C, IMAGES } from "@/data/content";
import { scrollToId } from "@/utils/scroll";
import Reveal, { SectionHeader } from "@/components/Reveal";

const ICONS = {
    packagesearch: PackageSearch,
    factory: Factory,
    cart: ShoppingCart,
    clipboard: ClipboardCheck,
    globe: Globe,
    ship: Ship,
    truck: Truck,
    target: Target,
};

export default function Services() {
    const { L } = useLanguage();

    const requestService = (title) => {
        window.dispatchEvent(
            new CustomEvent("llb-request-service", { detail: L(title) })
        );
        scrollToId("contact");
    };

    return (
        <section
            id="services"
            className="border-y border-gold-line/40 bg-ink-2/40 py-24 sm:py-32"
        >
            <div className="mx-auto max-w-7xl px-6">
                <SectionHeader
                    eyebrow={L(C.services.eyebrow)}
                    title={L(C.services.title)}
                />

                <div className="mt-14 grid gap-5 sm:grid-cols-2 lg:grid-cols-4">
                    {C.services.items.map((item, i) => {
                        const Icon = ICONS[item.icon];
                        const featured = item.id === "shipping";
                        return (
                            <Reveal key={item.id} delay={0.05 * (i % 4)} className="h-full">
                                <article
                                    data-testid={`service-card-${item.id}`}
                                    className={`group relative flex h-full flex-col overflow-hidden rounded-2xl border border-gold-line bg-ink-3/70 p-7 transition-all duration-300 hover:-translate-y-1 hover:border-gold-line-strong hover:shadow-[0_0_25px_rgba(212,175,55,0.15)] ${
                                        featured ? "" : ""
                                    }`}
                                >
                                    {featured && (
                                        <>
                                            <img
                                                src={IMAGES.port}
                                                alt=""
                                                className="absolute inset-0 h-full w-full object-cover opacity-20 transition-opacity duration-500 group-hover:opacity-30"
                                            />
                                            <div className="absolute inset-0 bg-gradient-to-t from-ink via-ink/80 to-ink/40" />
                                        </>
                                    )}
                                    <div className="relative flex items-start justify-between">
                                        <span className="grid size-12 place-items-center rounded-full border border-gold-line-strong text-brass transition-colors group-hover:bg-brass group-hover:text-ink">
                                            <Icon className="size-5" />
                                        </span>
                                        <span className="font-mono-x text-sm text-gold-line-strong transition-colors group-hover:text-brass">
                                            {item.num}
                                        </span>
                                    </div>
                                    <h3
                                        className="font-display relative mt-6 text-lg font-bold text-txt-1 sm:text-xl"
                                        dir="auto"
                                    >
                                        {L(item.title)}
                                    </h3>
                                    <p className="relative mt-3 flex-1 text-sm leading-relaxed text-txt-2">
                                        {L(item.desc)}
                                    </p>
                                    <button
                                        data-testid={`service-cta-${item.id}`}
                                        onClick={() => requestService(item.title)}
                                        className="relative mt-6 flex items-center gap-1.5 text-xs font-bold text-brass transition-colors hover:text-brass-bright"
                                    >
                                        {L(C.services.ctaLabel)}
                                        <ArrowUpRight className="size-3.5 transition-transform group-hover:rotate-45" />
                                    </button>
                                </article>
                            </Reveal>
                        );
                    })}
                </div>
            </div>
        </section>
    );
}
